// Client-side API for shared calendars.
//
// Schema (see supabase/schema.sql section 6):
//   shared_calendars        (id, name, owner_id, created_at)
//   shared_calendar_members (calendar_id, user_id, joined_at)
//   shared_calendar_invites (token, calendar_id, created_by, created_at)
//   calendar_events.shared_calendar_id  -- NULL for personal events
//
// All functions assume the caller is authenticated; RLS enforces access.

import { supabase } from './supabase';

// Base URL used to build invite links. We use the current origin + path so the
// link reopens the app and gets picked up by App.js on load.
const buildInviteUrl = (token) => {
  const { origin, pathname } = window.location;
  const base = `${origin}${pathname.replace(/\/+$/, '')}`;
  return `${base}?joinCalendar=${encodeURIComponent(token)}`;
};

const randomToken = () => {
  // 24 random base36 chars → ~120 bits of entropy.
  const arr = new Uint8Array(24);
  (window.crypto || window.msCrypto).getRandomValues(arr);
  return Array.from(arr, (b) => b.toString(36).padStart(2, '0')).join('').slice(0, 28);
};

// Returns an array of { id, name, ownerId, isOwner, createdAt }.
export async function listSharedCalendars() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];
  const { data, error } = await supabase
    .from('shared_calendars')
    .select('id, name, owner_id, created_at')
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data || []).map((r) => ({
    id: r.id,
    name: r.name,
    ownerId: r.owner_id,
    isOwner: r.owner_id === user.id,
    createdAt: r.created_at
  }));
}

// Create a new shared calendar owned by the current user via a SECURITY
// DEFINER RPC that also adds the creator as a member and provisions a
// reusable invite token.
// `options.withBirthdays` — create with a dedicated birthday list attached.
// Returns { id, name, inviteToken, hasBirthdays }.
export async function createSharedCalendar(name, options = {}) {
  const trimmed = (name || '').trim();
  if (!trimmed) throw new Error('Name is required');

  const { data, error } = await supabase.rpc('create_shared_calendar', {
    calendar_name: trimmed,
    with_birthdays: !!options.withBirthdays
  });
  if (error) throw error;
  // RPC returns a setof row; supabase-js gives us an array.
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) throw new Error('No row returned from create_shared_calendar');
  return {
    id: row.id,
    name: row.name,
    inviteToken: row.invite_token,
    hasBirthdays: !!row.has_birthdays
  };
}

// Update per-calendar birthday settings on the shared_calendars row.
// Only the owner should realistically call this (enforced client-side for
// UX); RLS also limits updates to the owner.
export async function updateSharedCalendarBirthdaySettings(calendarId, patch) {
  const row = {};
  if (patch.hasBirthdays !== undefined) row.has_birthdays = !!patch.hasBirthdays;
  if (patch.birthdayLinked !== undefined) row.birthday_linked = !!patch.birthdayLinked;
  if (patch.birthdayCategoryId !== undefined) row.birthday_category_id = patch.birthdayCategoryId || 'purple';
  if (Object.keys(row).length === 0) return;
  const { error } = await supabase
    .from('shared_calendars')
    .update(row)
    .eq('id', calendarId);
  if (error) throw error;
}

// Fetch (or return cached) the reusable invite URL for a calendar the caller owns.
export async function getInviteUrl(calendarId) {
  const { data, error } = await supabase
    .from('shared_calendar_invites')
    .select('token')
    .eq('calendar_id', calendarId)
    .maybeSingle();
  if (error && error.code !== 'PGRST116') throw error;
  if (!data) {
    // No token yet — create one (owner only; RLS enforces this).
    const { data: { user } } = await supabase.auth.getUser();
    const token = randomToken();
    const { error: insErr } = await supabase
      .from('shared_calendar_invites')
      .insert({ token, calendar_id: calendarId, created_by: user.id });
    if (insErr) throw insErr;
    return buildInviteUrl(token);
  }
  return buildInviteUrl(data.token);
}

// Join a shared calendar via an invite token. Returns the calendar id.
export async function joinSharedCalendarByToken(token) {
  const { data, error } = await supabase.rpc('join_shared_calendar', {
    invite_token: token
  });
  if (error) throw error;
  return data; // uuid
}

// Delete a shared calendar (owner only). Cascades to members/invites/events.
export async function deleteSharedCalendar(calendarId) {
  const { error } = await supabase
    .from('shared_calendars')
    .delete()
    .eq('id', calendarId);
  if (error) throw error;
}

// List members of a shared calendar the caller belongs to.
// Returns [{ userId, email, displayName, isOwner, joinedAt }].
export async function listSharedCalendarMembers(calendarId) {
  const { data, error } = await supabase.rpc('list_shared_calendar_members', {
    cid: calendarId
  });
  if (error) throw error;
  return (data || []).map((r) => ({
    userId: r.user_id,
    email: r.email,
    displayName: r.display_name,
    avatarEmoji: r.avatar_emoji,
    avatarColor: r.avatar_color,
    avatarUrl: r.avatar_url,
    avatarKind: r.avatar_kind || 'emoji',
    isOwner: r.is_owner,
    joinedAt: r.joined_at,
    categoryId: r.category_id || null
  }));
}

// Set the current user's preferred label color in a shared calendar. Used
// as the default categoryId for events they create here (Quick-Add and
// the modal). Pass `null` to clear. Only the user themselves can call this
// for their own row (RLS enforces user_id = auth.uid()).
export async function setMyCalendarCategory(calendarId, categoryId) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not signed in');
  const { error } = await supabase
    .from('shared_calendar_members')
    .update({ category_id: categoryId || null })
    .eq('calendar_id', calendarId)
    .eq('user_id', user.id);
  if (error) throw error;
}

// Remove the current user from a shared calendar they are a member of.
export async function leaveSharedCalendar(calendarId) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not signed in');
  const { error } = await supabase
    .from('shared_calendar_members')
    .delete()
    .eq('calendar_id', calendarId)
    .eq('user_id', user.id);
  if (error) throw error;
}
